<template>
    <div class="assetssment__data__row assessments_row_outer" :class="{ canSkipQues: item.flagged }">
        <!-- Item Number -->
        <div class="data__number table__row__item">{{ index }}.</div>

        <!-- Question Text and Consonants -->
        <div class="data__test table__row__item">
            <label>{{ item.question }}</label>
            <span v-if="item.consonant">{{ formatConsonants(item.consonant) }}</span>
        </div>

        <!-- Score Radio Buttons -->
        <div class="data__score__checkbox table__row__item" :class="{ disabled_tab: ['pending', 'processing'].includes(transcriptionStatus) === true }">
            <div class="point__checkbox" @click.prevent="handleScoreClick('1.0')">
                <input type="radio" :id="`point_${item.attemptid}_1`" value="1.0" readonly class="markscore"
                    :checked="score === '1.0'" :disabled="['pending', 'processing'].includes(transcriptionStatus) || !assessment?.is_latest || !canConductAssessment" />
                <label :for="`point_${item.attemptid}_1`">1 Point</label>
            </div>
            <div class="point__checkbox" v-if="group.id <= 3" @click.prevent="handleScoreClick('0.5')">
                <input type="radio" :id="`point_${item.attemptid}_2`" value="0.5" readonly class="markscore"
                    :checked="score === '0.5'" :disabled="['pending', 'processing'].includes(transcriptionStatus) || !assessment?.is_latest || !canConductAssessment" />
                <label :for="`point_${item.attemptid}_2`">1/2 Point</label>
            </div>
            <div class="point__checkbox" readonly @click.prevent="handleScoreClick('0.0')">
                <input type="radio" :id="`point_${item.attemptid}_3`" value="0.0" class="markscore"
                    :checked="score === '0.0'" :disabled="['pending', 'processing'].includes(transcriptionStatus) || !assessment?.is_latest || !canConductAssessment" />
                <label :for="`point_${item.attemptid}_3`">0 Point</label>
            </div>
        </div>

        <!-- Action Buttons: Audio, Flag, Transcribe -->
        <div class="data__action__options table__row__item">
            <div class="audio__video__options">
                <div class="audio__btn">
                    <a href="javascript:void(0)" @click="handleRecordAudio">
                        <img :src="playIcon" v-if="audio || !canConductAssessment" />
                        <img :src="micIcon" v-else />
                    </a>
                </div>
                <div class="flag__btn" :class="{ 'red__flag': flagged, disabled: !canConductAssessment }">
                    <a href="javascript:void(0)" class="flagUnFlagQuestion" @click="updateFlagged"
                        :class="{ disabled: !canConductAssessment }">
                        <img :src="flagIcon" />
                    </a>
                </div>
            </div>
            <div class="action__add__note__btn">
                <a href="javascript:void(0)" class="triggerTranscribe" @click="handleTranscribe">
                    <span class="btn__icon">
                        <img :src="addNoteIcon" />
                    </span>
                    <span class="btn__text">Transcribe</span>
                </a>
            </div>
        </div>
    </div>
</template>

<script setup>
import { formatConsonants } from '@/utils/format-helpers';
import { computed } from 'vue';
import playIcon from '@/assets/images/audio/assessment/play.png';
import micIcon from '@/assets/images/audio/assessment/mic.png';
import flagIcon from '@/assets/images/flag.png';
import addNoteIcon from '@/assets/images/action-add-note.png';
import { showDialog } from '@/utils/swal';
import { authrizedPermissions } from '@/utils/permissions';

// Props from parent component
const props = defineProps({
    assessment: Object,
    item: Object,
    index: Number,
    assessmentId: Number,
    organizationId: Number,
    group: Object,
    canConductAssessment: Boolean,
});

// Events emitted
const emit = defineEmits(['scored', 'flagged', 'savedAudio', 'recordAudio', 'transcribe']);

// Reactive references to item properties
const score = computed({
    get: () => props.item?.score,
    set: (val) => {
        emit('scored', { type: 'score', id: props.item.attemptid, value: val });
    }
});

const flagged = computed({
    get: () => props.item?.flagged,
    set: (val) => {
        emit('flagged', { type: 'flagged', id: props.item.attemptid, value: val || null });
    }
});

const audio = computed(() => props.item?.audio);

const updateScore = (value) => {
    score.value = (score.value === value) ? null : value;
};

function handleScoreClick(value) {
    if (['pending', 'processing'].includes(transcriptionStatus.value)) {
        return;
    }

    if(!props.assessment?.is_latest) {
        showDialog({ icon: 'warning', title: "Assessment Completed", html: "This assessment has been marked as completed and may no longer be changed." });
        return;
    }

    if (!props.canConductAssessment) {
        let message = `You don’t have permission perform this action.`;
        const learner = props.assessment?.learner;

        if (learner?.is_administrator) {
            message = `You don’t have permission to score this assessment because it was created before you were granted administrator access.`;
        } else if (learner?.is_authorized_user) {
            if (learner?.permissions?.[authrizedPermissions.conduct_view_assessments]) {
                message = `You don’t have permission to score this assessment because it was created before you were granted group access.`;
            } else if (!learner?.permissions?.[authrizedPermissions.conduct_view_assessments]) {
                message = `Assessment available to you through group access. You don’t have permission perform this action.`;
            }
        }

        showDialog({ icon: 'warning', html: message });
        return;
    }    

    updateScore(value);
}

// Toggle flagged status
const updateFlagged = () => {
    if(!props.assessment?.is_latest) {
        showDialog({ icon: 'warning', title: "Assessment Completed", html: "This assessment has been marked as completed and may no longer be changed." });
        return;
    }

    if (!props.canConductAssessment) {
        let message = `You don’t have permission perform this action.`;
        const learner = props.assessment?.learner;

        if (learner?.is_administrator) {
            message = `You don’t have permission to flag/unflag this assessment items because it was created before you were granted administrator access.`;
        } else if (learner?.is_authorized_user) {
            if (learner?.permissions?.[authrizedPermissions.conduct_view_assessments]) {
                message = `You don’t have permission to flag/unflag this assessment items because it was created before you were granted group access.`;
            } else if (!learner?.permissions?.[authrizedPermissions.conduct_view_assessments]) {
                message = `Assessment available to you through group access. You don’t have permission perform this action.`;
            }
        }

        showDialog({ icon: 'warning', html: message });
        return;
    }

    flagged.value = flagged.value ? null : 1;
};

// Trigger audio recording or playback
const handleRecordAudio = () => {
    if(!props.assessment?.is_latest) {
        showDialog({ icon: 'warning', title: "Assessment Completed", html: "This assessment has been marked as completed and may no longer be changed." });
        return;
    }

    if (!audio.value && !props.canConductAssessment) {
        showDialog({ icon: 'warning', title: "Assessment Completed", html: "You do not have access to perform this assessment." });
        return;
    }

    emit('recordAudio', { item: props.item, index: props.index });
};

// Trigger audio recording or playback
const handleTranscribe = () => {
    emit('transcribe', { item: props.item, index: props.index });
};

const transcriptionStatus = computed(() => props.item?.transcription_status);
</script>
